<template>
	<DialogControl v-model="open" :title="t('AdditionnalPresence')" :subtitle="product?.label || ''" icon="add_location_alt" max-width="780" :attach="overlayTarget">
		<v-table v-if="lines.length" density="compact" class="bg-transparent">
			<thead>
				<tr>
					<th>{{ t('Quantity') }}</th>
					<th>{{ t('Location') }}</th>
					<th>{{ t('LocationHighlight') }}</th>
					<th class="text-end">{{ t('UC') }}</th>
					<th />
				</tr>
			</thead>
			<tbody>
				<tr v-for="(line, index) in lines" :key="index">
					<td>{{ format.number(line.volume) }} {{ labelOf(snapshot.refs.containers, line.productContainerId) }}</td>
					<td>{{ labelOf(snapshot.refs.locations, line.locationPromoId) }}</td>
					<td>{{ labelOf(snapshot.refs.highlights, line.locationHighlightId) }}</td>
					<td class="text-end font-weight-bold">{{ format.uc(line.volumeUC) }}</td>
					<td class="text-end">
						<v-btn v-if="editable" icon="delete" size="x-small" variant="text" color="error" @click="ops.removeAdditional(offerRef, productId, kind, line)" />
					</td>
				</tr>
			</tbody>
		</v-table>
		<div v-else class="text-body-medium text-medium-emphasis">{{ t('PromoNoAdditionalPresence') }}</div>
		<v-sheet v-if="editable" color="surface-variant" rounded="lg" class="pa-3">
			<v-row density="compact" align="center">
				<v-col cols="6" sm="2">
					<v-text-field v-model.number="draft.volume" type="number" min="0" inputmode="numeric" :label="t('Quantity')" variant="outlined" density="compact" hide-details />
				</v-col>
				<v-col cols="6" sm="3">
					<v-select :menu-props="menuProps" v-model="draft.containerId" :items="containerItems" item-title="label" item-value="id" :label="t('PromotionalUnit')" variant="outlined" density="compact" hide-details />
				</v-col>
				<v-col cols="12" sm="3">
					<v-select :menu-props="menuProps" v-model="draft.locationId" :items="locationItems" item-title="label" item-value="id" :label="t('Location')" variant="outlined" density="compact" hide-details />
				</v-col>
				<v-col cols="12" sm="2">
					<v-select :menu-props="menuProps" v-model="draft.highlightId" :items="highlightItems" item-title="label" item-value="id" :label="t('LocationHighlight')" variant="outlined" density="compact" hide-details />
				</v-col>
				<v-col cols="12" sm="2" class="d-flex align-center justify-end ga-2">
					<v-chip size="small" color="primary" label>{{ previewUC ? format.uc(previewUC) : '—' }}</v-chip>
					<v-btn icon="add" color="primary" variant="tonal" size="small" :disabled="!draft.volume || !draft.containerId || !draft.locationId" @click="add" />
				</v-col>
			</v-row>
		</v-sheet>
		<template #actions>
			<v-btn color="primary" variant="tonal" @click="open = false">{{ t('Close') }}</v-btn>
		</template>
	</DialogControl>
</template>

<script setup>
import { computed, inject, reactive, watch } from 'vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { containersOf, getProduct } from '@/utils/domain/promo/promoModel';
import { additionalOf } from '@/utils/domain/promo/promoAdditional';
import { toUC } from '@/utils/domain/promo/promoUnits';

const open = defineModel({ type: Boolean, default: false });
const props = defineProps({
	campaign: { type: Object, required: true },
	offer: { type: Object, required: true },
	offerRef: { type: Object, required: true },
	productId: { type: Number, required: true },
	kind: { type: String, required: true },
	editable: { type: Boolean, default: false },
});

const { snapshot, ops, format, t } = usePromoStep();
const overlayTarget = inject('overlayTarget', null);
const menuProps = { attach: overlayTarget || undefined };
const draft = reactive({ volume: 0, containerId: 0, locationId: 0, highlightId: 0 });

const product = computed(() => getProduct(snapshot.value, props.productId));
const lines = computed(() => additionalOf(snapshot.value, props.offerRef, props.productId, props.kind));
const previewUC = computed(() => toUC(snapshot.value, props.productId, Number(draft.volume) || 0, draft.containerId));

const empty = computed(() => ({ id: 0, label: '...' }));
const containerItems = computed(() => [empty.value, ...containersOf(snapshot.value, props.offer, props.productId)]);
const locationItems = computed(() => [empty.value, ...snapshot.value.refs.locations]);
const highlightItems = computed(() => [empty.value, ...snapshot.value.refs.highlights]);
const labelOf = (items, id) => items.find((item) => item.id === id)?.label || '—';

const reset = () => Object.assign(draft, { volume: 0, containerId: 0, locationId: 0, highlightId: 0 });

function add() {
	if (ops.addAdditional(props.offerRef, props.productId, props.kind, { ...draft })) reset();
}

watch(open, (value) => value && reset());
</script>
